// Item-store catalog (display only — see server/store.mjs for the authoritative
// prices). 'emote' items unlock that emote id in the wheel config once owned
// (see src/ui/EmoteWheel.ts); 'cueColor' items unlock (and can be equipped as) a
// custom cue-ball colour; 'music' items unlock a curated track that can be
// dropped straight into the room's shared playlist; 'nickname' is a priced
// action (not owned/inventoried — every use is charged again); 'stub' items are
// cosmetic placeholders for future features — purchasable and persisted, but
// with no effect yet.

export type StoreItemKind = 'emote' | 'cueColor' | 'music' | 'nickname' | 'stub';

export interface StoreItemDef {
  id: string;
  kind: StoreItemKind;
  label: string;
  glyph: string;
  price: number;
  /** cueColor only: the hex swatch (no '#') — must match server/store.mjs CUE_COLORS. */
  hex?: string;
  /** music only: the YouTube video id this track adds once owned. */
  videoId?: string;
}

export const STORE_ITEMS: StoreItemDef[] = [
  // premium voice-line / emoji emotes (ids match src/emotes/catalog.ts)
  { id: 'v_kabi', kind: 'emote', label: '아 까비', glyph: '😩', price: 100 },
  { id: 'v_niceshot', kind: 'emote', label: '나이스 샷', glyph: '🎯', price: 100 },
  { id: 'v_goodshot', kind: 'emote', label: '굿샷', glyph: '👍', price: 100 },
  { id: 'v_taeyang', kind: 'emote', label: '태양아 잘 쳤어', glyph: '🌞', price: 150 },
  { id: 'v_jjalne', kind: 'emote', label: '이런 짤네', glyph: '🙄', price: 100 },
  { id: 'v_jjalne2', kind: 'emote', label: '이런 짤네 (2)', glyph: '🙄', price: 100 },
  { id: 'v_angaju', kind: 'emote', label: '안 가주?', glyph: '🤨', price: 100 },
  { id: 'v_jayu', kind: 'emote', label: '자유쥬?', glyph: '❓', price: 100 },
  { id: 'v_mwonya', kind: 'emote', label: '뭐냐 이게', glyph: '😵', price: 100 },
  { id: 'v_gensei', kind: 'emote', label: '겐세이 하지마', glyph: '✋', price: 150 },
  { id: 'v_coffee', kind: 'emote', label: '냉커피? 콜라?', glyph: '☕', price: 100 },
  { id: 'mindblown', kind: 'emote', label: 'Wow', glyph: '🤯', price: 80 },
  { id: 'salt', kind: 'emote', label: 'Salty', glyph: '🧂', price: 80 },
  // custom cue-ball colours (equip once owned)
  { id: 'cue_crimson', kind: 'cueColor', label: 'Crimson', glyph: '🔴', price: 150, hex: 'c8264a' },
  { id: 'cue_azure', kind: 'cueColor', label: 'Azure', glyph: '🔵', price: 150, hex: '2a6fdb' },
  { id: 'cue_emerald', kind: 'cueColor', label: 'Emerald', glyph: '🟢', price: 150, hex: '1f9d63' },
  { id: 'cue_violet', kind: 'cueColor', label: 'Violet', glyph: '🟣', price: 150, hex: '7b4fd6' },
  { id: 'cue_gold', kind: 'cueColor', label: 'Gold', glyph: '🟡', price: 250, hex: 'd9a441' },
  { id: 'cue_obsidian', kind: 'cueColor', label: 'Obsidian', glyph: '⚫', price: 250, hex: '2b2b31' },
  // curated music tracks — add straight to the room's shared playlist once owned
  // (held back from the free default queue in src/ui/MusicPanel.ts)
  { id: 'music_fx_chu', kind: 'music', label: 'f(x) — Chu~', glyph: '🎵', price: 60, videoId: 'Cnz2n5VcV4Q' },
  { id: 'music_4minute', kind: 'music', label: '4Minute — Hot Issue', glyph: '🎵', price: 60, videoId: 'kb4iKfnW2Yo' },
  { id: 'music_ss501', kind: 'music', label: 'SS501 — Love Ya', glyph: '🎵', price: 60, videoId: 'wqHX9YmqeyQ' },
  // one-time nickname change (priced action, not an owned item)
  { id: 'nickname_change', kind: 'nickname', label: 'Change nickname', glyph: '✏️', price: 200 },
  // stub items — coming soon, no gameplay effect yet
  { id: 'table_felt_gold', kind: 'stub', label: 'Gold table felt', glyph: '🟨', price: 500 },
  { id: 'avatar_frame_neon', kind: 'stub', label: 'Neon avatar frame', glyph: '💠', price: 300 },
];

export const STORE_ITEMS_BY_ID: Map<string, StoreItemDef> = new Map(STORE_ITEMS.map((i) => [i.id, i]));

/** Store item ids that gate an emote (used to lock it out of the wheel config until owned). */
export const PREMIUM_EMOTE_IDS: Set<string> = new Set(STORE_ITEMS.filter((i) => i.kind === 'emote').map((i) => i.id));
